import React from 'react';
import {withRouter, WithRouterProps} from 'react-router';
import {isNumber} from 'lodash';

import {Badge, Button, Icon, LoadingSpinner, Text} from '@albert-io/atomic';
import {IconProp} from '@albert-io/atomic/atoms/Icon/Icon.react';

import {ProficiencyChartBadges} from 'client/Reports/components/Table/Columns/ProficiencyChart/ProficiencyChart';

import {SkillSubjectGroup} from './StudentSkillPath.types';
import {calculateDueDate, DueDateResult} from 'lib/timeUtils';
import classNames from 'classnames';

interface StudentSkillPathListProps {
  skillSubjectGroups: SkillSubjectGroup[];
  isLoading: boolean;
  isMobile: boolean;
}

function DueDateBadge({dueDateInfo}: {dueDateInfo: DueDateResult}) {
  const {isPastDue, color} = dueDateInfo;
  const icon: IconProp = isPastDue ? ['far', 'exclamation-triangle'] : ['far', 'clock'];
  return (
    <Badge
      status={color}
      emphasis='subtle'
      icon={icon}
      className={classNames({
        'content-primary': !isPastDue
      })}
    >
      {dueDateInfo.text}
    </Badge>
  );
}

interface SkillPathItemProps extends WithRouterProps<{}> {
  guideLevelId: string;
  name: string;
  code?: string | null;
  dueDate?: string | null;
  myProficiencyLevel?: number | null;
  isMobile?: boolean;
}

const SkillPathItemInner = ({
  router,
  guideLevelId,
  name,
  code,
  dueDate,
  myProficiencyLevel,
  isMobile
}: SkillPathItemProps) => {
  const handleClick = () => {
    router.push(`/adaptive/skill/${guideLevelId}`);
  };

  const dueDateInfo = dueDate ? calculateDueDate(dueDate) : null;

  return (
    <div
      className={classNames(
        'flex cursor-pointer flex-wrap items-center justify-between p-x0-half hover:background-subtle',
        {
          'gap-x0-half': isMobile
        }
      )}
      role='button'
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter') handleClick();
      }}
    >
      <div className=''>
        <Text size='m' color='primary' className='mr-x1'>
          {name}
        </Text>
        <Text size='m' color='tertiary'>
          ({code})
        </Text>
      </div>
      <div className='flex items-center gap-x1-half'>
        <div className='min-w-fit'>{dueDateInfo && <DueDateBadge dueDateInfo={dueDateInfo} />}</div>
        <ProficiencyChartBadges
          proficiencyLevel={myProficiencyLevel}
          showNotStarted={false}
          showLabel={false}
          badgeSize={16}
          iconGap={0}
          className='proficiency-chart__student_browsing'
          withTooltip={isNumber(myProficiencyLevel)}
        />
      </div>
    </div>
  );
};

const SkillPathItem = withRouter(SkillPathItemInner);

interface BrowseAllSkillsLinkProps extends WithRouterProps<{}> {
  href: string;
}

const BrowseAllSkillsLinkInner = ({router, href}: BrowseAllSkillsLinkProps) => (
  <Button
    color='secondary'
    variant='outlined'
    size='s'
    onClick={() => router.push(href)}
    roundness='pill'
    className='w-[149px]'
  >
    <Text size='m' color='inherit' className='font-normal'>
      Browse all skills
    </Text>
    <Icon className='ml-x1-quarter' icon={['far', 'arrow-up-right']} />
  </Button>
);

const BrowseAllSkillsLink = withRouter(BrowseAllSkillsLinkInner);

function SkillPathNoAccessState() {
  return (
    <div className='flex flex-col items-center justify-center gap-x2 border border-border-subtle p-x4 text-center'>
      <div className='flex h-x5 w-x5 items-center justify-center rounded-full background-bold'>
        <Icon icon={['far', 'sparkles']} color='tertiary' />
      </div>
      <Text size='m' color='secondary'>
        Adaptive subjects and skills will appear here if you have access.{' '}
      </Text>
    </div>
  );
}

export function StudentSkillPathList({
  skillSubjectGroups,
  isLoading,
  isMobile
}: StudentSkillPathListProps) {
  if (isLoading) {
    return (
      <div className='flex items-center justify-center p-x4'>
        <LoadingSpinner size={3} />
      </div>
    );
  }

  if (skillSubjectGroups.length === 0) {
    return (
      <div
        className={classNames('flex flex-col gap-x4 border-t border-t-border-subtle', {
          'p-x4': !isMobile,
          'p-x2': isMobile
        })}
      >
        <SkillPathNoAccessState />
      </div>
    );
  }

  const sortedSubjectGroups = [...skillSubjectGroups].sort((a, b) => {
    const aCount = a.skillGuides.reduce((sum, s) => sum + s.items.length, 0);
    const bCount = b.skillGuides.reduce((sum, s) => sum + s.items.length, 0);
    return bCount - aCount;
  });

  return (
    <div
      className={classNames('flex flex-col gap-x4 border-t border-t-border-subtle', {
        'p-x4': !isMobile,
        'p-x2': isMobile
      })}
    >
      {sortedSubjectGroups.map((group) => (
        <div key={group.subjectId} className='flex flex-col gap-x2'>
          <div className='flex items-center gap-x2'>
            <div className='flex h-x5 w-x5 items-center justify-center rounded-full background-special-subtle'>
              <Icon icon='sparkles' className='content-special!' />
            </div>
            <div className='flex flex-col'>
              <Text size='l' color='primary'>
                {group.subjectName}
              </Text>
              {group.skillGuides.length === 0 && (
                <Text size='s' color='tertiary'>
                  You can practice any skills within this subject.
                </Text>
              )}
            </div>
          </div>

          <div
            className={classNames('flex flex-col gap-x0-half', {
              'pl-x7': !isMobile
            })}
          >
            {group.skillGuides.map((guide) => (
              <div key={guide.guideId} className='mb-x1 flex flex-col'>
                {guide.guideName && guide.guideName.toLowerCase() !== 'adaptive' && (
                  <div className='p-x0-half'>
                    <Text size='s' color='primary' bold>
                      {guide.guideName}
                    </Text>
                  </div>
                )}
                <div className='flex flex-col gap-x0-half'>
                  {guide.items.map((item) => (
                    <SkillPathItem
                      key={item.id}
                      guideLevelId={item.id}
                      name={item.name}
                      code={item.code}
                      dueDate={item.dueDate}
                      myProficiencyLevel={item.myProficiencyLevel}
                      isMobile={isMobile}
                    />
                  ))}
                </div>
              </div>
            ))}
            <BrowseAllSkillsLink href={group.browseUrl} />
          </div>
        </div>
      ))}
    </div>
  );
}
